import { useMutation, useQuery } from "react-query";
import { useEffect, useState } from "react";
import queryString from "query-string";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import { useFormik } from "formik";
import * as Yup from "yup";

import { ExchangeFunds, EditIcon, DeleteIcon } from "../Icons";
import "./../../assets/scss/reports/cashbook.scss";
import useDebounce, { useQueryParams, useScrollTop } from "../../utils/hooks";
import { useAuth } from "../../hooks/useAuth";
import ModalLoader from "../utils/ModalLoader";
import NoTableItem from "../utils/NoTableItem";
import { fetchActionsUtil } from "../../utils/helpers";
import { toast } from "react-toastify";
import ConfirmDialog from "../ConfirmDialogue";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import {
  Modal,
  Form,
  Button,
  InputGroup,
  Spinner,
  Dropdown,
  Table,
} from "react-bootstrap";
import CachedIcon from "mdi-react/CachedIcon";

// Empty State Component
function EmptyState({ hasFilters, onCreateNew, onClearFilters }) {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center py-5 my-5">
      <div className="text-center mb-4">
        <div
          className="d-flex align-items-center justify-content-center mb-3"
          style={{
            height: "80px",
          }}
        >
          <NoTableItem size={40} color="#6c757d" />
        </div>
      </div>

      <div className="d-flex gap-2">
        <Button
          variant="primary"
          onClick={onCreateNew}
          className="d-flex align-items-center gap-2"
        >
          <ExchangeFunds size={16} color="white" />
          Create New Item Measurement
        </Button>

        {hasFilters && (
          <Button variant="outline-secondary" onClick={onClearFilters}>
            Clear Filters
          </Button>
        )}
      </div>
    </div>
  );
}

function ItemMeasurementManagement() {
  useScrollTop();

  const initialFilterParams = {
    Size: "",
  };

  const { backendUrl, user: authUser } = useAuth();

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedItemMeasurement, setSelectedItemMeasurement] = useState(null);

  const [queryParams, setQueryParams] = useQueryParams({
    page: 1,
    limit: 40,
    ...initialFilterParams,
  });

  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);

  useEffect(() => {
    setQueryParams(query => ({
      ...query,
      ...debouncedFilterParams,
    }));
  }, [debouncedFilterParams, setQueryParams]);

  // Fetch ItemMeasurement data
  const { data, refetch, isFetching } = useQuery(
    ["ITEM-MEASUREMENT-MANAGEMENT", queryParams],
    () =>
      fetchActionsUtil(
        `${backendUrl}/api/item-measurement?${queryString.stringify(
          queryParams
        )}`,
        "GET",
        authUser.token
      ),
    {
      keepPreviousData: true,
      select: data => ({
        ...data,
        itemMeasurement: data?.itemMeasurements || [],
      }),
    }
  );

  // Delete ItemMeasurement mutation
  const deleteItemMeasurementMutation = useMutation(
    id =>
      fetchActionsUtil(
        `${backendUrl}/api/item-measurement/${id}`,
        "DELETE",
        authUser.token
      ),
    {
      onSuccess: () => {
        toast.success("Item Measurement deleted successfully");
        refetch();
      },
      onError: ({ message }) => {
        toast.error(message || "Unable to delete Item Measurement: Try again");
      },
    }
  );

  // ItemMeasurement validation schema
  const itemMeasurementValidationSchema = Yup.object().shape({
    Size: Yup.string()
      .required("Size is required")
      .min(1, "Size must be at least 1 character"),
    Quantity: Yup.number()
      .required("Quantity is required")
      .min(0, "Quantity must be 0 or greater"),
  });

  const handleDeleteItemMeasurement = async item => {
    if (
      await ConfirmDialog({
        title: `Delete Item Measurement`,
        description: `Are you sure you want to DELETE ${item.Size}?`,
      })
    ) {
      deleteItemMeasurementMutation.mutate(item.id);
    }
  };

  const handleFilterParamsChange = e => {
    setFilterParams({
      ...filterParams,
      [e.target.name]: e.target.value,
    });
  };

  // Check if any filters are applied
  const hasActiveFilters = Boolean(filterParams.Size);

  // Clear all filters
  const clearAllFilters = () => {
    setFilterParams(initialFilterParams);
    setQueryParams({
      page: 1,
      limit: queryParams.limit,
      ...initialFilterParams,
    });
  };

  const hasData = data?.itemMeasurement && data.itemMeasurement.length > 0;

  return (
    <main className="cash-book table-sticky-header">
      <div className="content">
        <header className="container">
          <h1>
            Item Measurement Management
            <button
              title="Refresh"
              onClick={() => refetch()}
              className="btn text-primary"
            >
              <CachedIcon />
            </button>
          </h1>

          <div className="actions mr-5">
            <div>
              <InputGroup>
                <Form.Control
                  name="Size"
                  placeholder="Search Size..."
                  value={filterParams?.Size}
                  onChange={e => handleFilterParamsChange(e)}
                />
                <InputGroup.Text>
                  <MagnifyIcon />
                </InputGroup.Text>
              </InputGroup>
            </div>

            <button
              onClick={() => setOpenCreateModal(true)}
              className="btn bg-primary text-white"
            >
              Create New <ExchangeFunds color="white" />
            </button>
          </div>
        </header>

        {/* Show table only when there's data */}
        {hasData && (
          <div>
            <Table borderless striped className="product-table container">
              <thead className="position-sticky top-0">
                <tr>
                  <th />
                  <th>S/N</th>
                  <th>Size</th>
                  <th>Quantity</th>
                </tr>
              </thead>
              <tbody>
                {data?.itemMeasurement?.map((item, i) => (
                  <tr key={item.id || i}>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle
                          variant=""
                          className="bg-white border-0"
                          bsPrefix="print more"
                        >
                          <DotsVerticalIcon />
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                          popperConfig={{ strategy: "fixed" }}
                          renderOnMount
                          className="dropdown-with-icons"
                        >
                          <Dropdown.Item
                            as="div"
                            className="p-cursor"
                            onClick={() => {
                              setSelectedItemMeasurement(item);
                              setOpenEditModal(true);
                            }}
                          >
                            <EditIcon /> Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            as="div"
                            onClick={() => handleDeleteItemMeasurement(item)}
                            className="p-cursor"
                          >
                            <DeleteIcon color="#94A3B8" />
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                    <td>
                      {(queryParams.page - 1) * queryParams.limit + i + 1}
                    </td>
                    <td>{item.Size}</td>
                    <td>{item.Quantity}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Show empty state when no data */}
        {!hasData && !isFetching && (
          <EmptyState
            hasFilters={hasActiveFilters}
            onCreateNew={() => setOpenCreateModal(true)}
            onClearFilters={clearAllFilters}
          />
        )}

        {/* Show legacy NoTableItem component only when there's data but empty results */}
        {hasData && data?.itemMeasurement?.length < 1 && (
          <NoTableItem queryParams={queryParams} />
        )}
      </div>

      <ModalLoader
        show={isFetching || deleteItemMeasurementMutation.isLoading}
      />

      {openCreateModal && (
        <AddEditItemMeasurementModal
          show={openCreateModal}
          onHide={() => setOpenCreateModal(false)}
          validationSchema={itemMeasurementValidationSchema}
          title="Create New Item Measurement"
          refetchData={refetch}
        />
      )}

      {openEditModal && selectedItemMeasurement && (
        <AddEditItemMeasurementModal
          show={openEditModal}
          onHide={() => {
            setOpenEditModal(false);
            setSelectedItemMeasurement(null);
          }}
          validationSchema={itemMeasurementValidationSchema}
          title="Edit Item Measurement"
          initialValues={selectedItemMeasurement}
          refetchData={refetch}
        />
      )}
    </main>
  );
}

export default ItemMeasurementManagement;

function AddEditItemMeasurementModal({
  show,
  onHide,
  validationSchema,
  title,
  initialValues = {
    Size: "",
    Quantity: "",
  },
  refetchData,
}) {
  const { backendUrl, user: authUser } = useAuth();
  const isEditMode = !!initialValues.id;

  // Create ItemMeasurement mutation
  const createItemMeasurementMutation = useMutation(
    payload =>
      fetchActionsUtil(
        `${backendUrl}/api/item-measurement`,
        "POST",
        authUser.token,
        payload
      ),
    {
      onSuccess: () => {
        toast.success("Item Measurement created successfully");
        refetchData();
        onHide();
      },
      onError: ({ message }) => {
        toast.error(message || "Unable to create Item Measurement: Try again");
      },
    }
  );

  // Update ItemMeasurement mutation
  const updateItemMeasurementMutation = useMutation(
    ({ id, ...payload }) =>
      fetchActionsUtil(
        `${backendUrl}/api/item-measurement/${id}`,
        "PUT",
        authUser.token,
        payload
      ),
    {
      onSuccess: () => {
        toast.success("Item Measurement updated successfully");
        refetchData();
        onHide();
      },
      onError: ({ message }) => {
        toast.error(message || "Unable to update Item Measurement: Try again");
      },
    }
  );

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    if (isEditMode) {
      updateItemMeasurementMutation.mutate(
        { id: initialValues.id, ...values },
        {
          onSuccess: () => {
            setSubmitting(false);
            resetForm();
          },
          onError: () => {
            setSubmitting(false);
          },
        }
      );
    } else {
      createItemMeasurementMutation.mutate(values, {
        onSuccess: () => {
          setSubmitting(false);
          resetForm();
        },
        onError: () => {
          setSubmitting(false);
        },
      });
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const isLoading =
    createItemMeasurementMutation.isLoading ||
    updateItemMeasurementMutation.isLoading;

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header closeButton>
        <Modal.Title className="h5">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Size *</Form.Label>
            <Form.Control
              type="text"
              name="Size"
              value={formik.values.Size}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.Size && formik.errors.Size}
              placeholder="Enter size"
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.Size}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Quantity *</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              name="Quantity"
              value={formik.values.Quantity}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.Quantity && formik.errors.Quantity}
              placeholder="Enter quantity"
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.Quantity}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button
              variant="secondary"
              onClick={onHide}
              disabled={formik.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={formik.isSubmitting || isLoading}
            >
              {formik.isSubmitting || isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {isEditMode ? "Updating..." : "Creating..."}
                </>
              ) : isEditMode ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
